import type { Ref } from 'vue'
import { ref, nextTick, onMounted, onUnmounted } from 'vue'
import throttle from 'lodash/throttle'
import debounce from 'lodash/debounce'

export const useIsElementOverflowing = (
  containerRef: Ref<HTMLElement | undefined>
) => {
  const isOverflowing = ref(false)

  // Reusable check function
  const checkOverflow = () => {
    if (containerRef.value) {
      isOverflowing.value =
        containerRef.value.scrollWidth > containerRef.value.offsetWidth
    }
  }

  // Setup observers and event listeners
  let resizeObserver: ResizeObserver | null = null

  const throttledUpdateEllipsedRecipients = throttle(() => {
    checkOverflow()
    nextTick(checkOverflow)
  }, 200)

  const debouncedUpdateEllipsedRecipients = debounce(() => {
    checkOverflow()
    nextTick(checkOverflow)
  }, 200)

  const onResizeViewport = () => {
    throttledUpdateEllipsedRecipients()
    debouncedUpdateEllipsedRecipients()
  }

  onMounted(() => {
    if (containerRef.value) {
      // Initial check
      checkOverflow()

      // Watch for container resizing
      resizeObserver = new ResizeObserver(onResizeViewport)
      resizeObserver.observe(containerRef.value)

      // Optional: Watch for window resize
      window.addEventListener('resize', onResizeViewport)
    }
  })

  // Cleanup
  onUnmounted(() => {
    if (resizeObserver && containerRef.value) {
      resizeObserver.unobserve(containerRef.value)
    }
    window.removeEventListener('resize', onResizeViewport)
  })

  return {
    isOverflowing,
    checkOverflow,
  }
}
